/**
 * The game.
 *
 * Load the route and you are already in a level: no menu, no seed box, no
 * loadout. The level itself was generated on page load from a random seed,
 * compiled, and gated on being fully connected with at least two independent
 * routes from spawn to exit — so "random" here never means "unfair" or
 * "broken", it means the floor you are standing on has never existed before
 * and has already been proven walkable.
 */

import * as THREE from 'three';
import { generateLevel, type GeneratedLevel } from 'levelspec/generate';
import { loadMap, runOrder, MAPS, type MapEntry } from './library.ts';
import { Player } from '../three/player.ts';
import { applyTheme, buildWorld, disposeTree, type World } from './world.ts';
import { makePost } from './post.ts';
import { warmShaders } from './warm.ts';
import { Run } from './run.ts';
import { Hud } from './hud.ts';
import { TouchControls } from './touch.ts';
import { offer } from './cards.ts';
import { ENEMY_TYPES } from './enemies.ts';
import { loadScores, saveScore, type ScoreRow } from './scores.ts';
import { viewModel } from './viewmodel.ts';
import { report } from './telemetry.ts';
import { detectTier, qualityFor, Governor } from './quality.ts';
import { sound } from './audio.ts';
import { load as loadSettings, save as saveSettings } from './settings.ts';

const host = document.getElementById('app')!;

// Device pixel ratio is the single biggest fill-rate lever on a phone, and a
// shooter wants a steady frame far more than it wants crisp edges. The tier is
// a guess about the hardware; the governor is a measurement of it, and takes
// over within a couple of seconds of the first frame.
const quality = qualityFor(detectTier());
const governor = new Governor(quality);
const renderer = new THREE.WebGLRenderer({ antialias: quality.antialias, powerPreference: 'high-performance' });
renderer.setPixelRatio(governor.pixelRatio);
renderer.setSize(innerWidth, innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
/*
 * Filmic, because the world is now mostly one pale colour.
 *
 * Without tone mapping the renderer clips: a white wall with a light on it and
 * a white wall with two lights on it are both 1.0, so the second one is not
 * brighter, it is the same. That is fatal to architecture whose entire form is
 * carried by how much light falls on it. ACES rolls the top end off instead,
 * which keeps the shoulder between "lit" and "very lit" and leaves headroom
 * above the shell for the things that are supposed to glow.
 */
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;
host.append(renderer.domElement);

/*
 * Losing the graphics context, and getting it back.
 *
 * A phone takes the GPU away when it feels like it — another tab, a call, the
 * screen going off — and without these two lines the canvas goes black and
 * stays black for ever. Preventing the default on loss is what tells the
 * browser we intend to come back; three re-uploads what it needs on the next
 * frame because it keeps the data on the CPU side, and the floor is rebuilt
 * because a level half-way through re-uploading is not a level to play on.
 */
let contextLost = false;
renderer.domElement.addEventListener('webglcontextlost', (e) => {
  e.preventDefault();
  contextLost = true;
  setPaused(true);
  run.say('Graphics interrupted — hold on', 6);
});
renderer.domElement.addEventListener('webglcontextrestored', () => {
  contextLost = false;
  renderer.setPixelRatio(governor.pixelRatio);
  renderer.setSize(innerWidth, innerHeight);
  load(run.state.depth);
  // Straight back in: the interruption was not the player's decision, so
  // leaving them on a settings screen afterwards would be the game's fault
  // twice.
  setPaused(false);
  run.say('Back', 1.6);
});

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(78, innerWidth / innerHeight, 0.06, 400);
const player = new Player(camera);
const hud = new Hud(host);
const touch = new TouchControls(host);
const run = new Run(scene, player, quality.tier);
/*
 * The frame's finishing pass.
 *
 * Built once and given the scene and camera it will always be handed; the view
 * model still draws itself straight to the canvas afterwards, over a cleared
 * depth buffer, because a gun in your hands is not part of the world and
 * should not glow with it.
 */
const post = makePost(renderer, scene, camera, quality.tier);
const vm = viewModel();
// The run tells the view model what is about to be wanted; it is declared
// after the run, so the wiring happens here rather than at construction.
run.prefetch = (file) => vm.prefetch(file);
scene.add(camera);

let world: World | null = null;
let lights: THREE.Object3D[] = [];
let seed = freshSeed();

/**
 * Generating a floor takes a few hundred milliseconds of compile and bake, and
 * that is a few hundred milliseconds of blank screen if it happens before the
 * first paint. So the page ships a splash in its own markup, and the work
 * starts on the next frame — the player sees something within one frame either
 * way, which is what "instant" actually has to mean here.
 */
function afterPaint(fn: () => void): void {
  requestAnimationFrame(() => requestAnimationFrame(fn));
}

function dropSplash(): void {
  const s = document.getElementById('splash');
  if (!s) return;
  s.style.opacity = '0';
  setTimeout(() => s.remove(), 260);
}

function freshSeed(): string {
  const a = new Uint32Array(2);
  crypto.getRandomValues(a);
  return `${a[0].toString(36)}${a[1].toString(36)}`;
}

/**
 * Everything a run is, from one word.
 *
 * The floors were already generated from a seed; the order they came in and
 * the cards you were offered were not, so two runs on the same seed were two
 * different runs and a seed was worth nothing. Both derive from it now, which
 * makes a run a thing you can hand somebody — and, more usefully, a thing you
 * can reproduce when it goes wrong.
 *
 * `?seed=` replays one. It is read once, at boot, and dropped as soon as the
 * player starts another, so a reload after a death is a new run rather than
 * the same one again.
 */
function hash32(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** A deterministic stream, for the choices a replay has to repeat. */
function stream(text: string): () => number {
  let a = hash32(text);
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const params = new URLSearchParams(location.search);
const requested = params.get('seed');

/**
 * Today's seed, the same for everybody, everywhere.
 *
 * `?daily` rather than a menu, because a menu is the thing this game does not
 * have. What it buys is the one social thing a single-player roguelite can
 * have for free: a run two people can compare without either of them having
 * to send the other a link first. The date is in the seed so it is legible in
 * a screenshot, and it is UTC so that "today" means the same day either side
 * of a timezone.
 */
function dailySeed(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = `${now.getUTCMonth() + 1}`.padStart(2, '0');
  const d = `${now.getUTCDate()}`.padStart(2, '0');
  return `daily-${y}${m}${d}`;
}

const daily = params.has('daily');

// -----------------------------------------------------------------------------

/** A floor built ahead of time while the card screen is up. */
/*
 * The next floor, made early.
 *
 * Both halves of it. Compiling the plan was already done behind the card
 * screen — 683ms on the heaviest map in the library, which is most of the
 * transition — but building the world was not, and that is another 244ms of
 * dead frame after the card is picked. `tools/perf.html` measures every other
 * interaction in the game at under ten milliseconds, so this is the only
 * hitch left, and it is the one you feel because it lands exactly as control
 * comes back.
 *
 * The world is geometry and colliders; nothing in it needs the renderer or
 * the scene, so it can be built while nobody is looking and added later. The
 * lights cannot — they want the renderer for shadow maps — so those stay in
 * `load`.
 */
let prefetched: { depth: number; gen: GeneratedLevel; world?: World } | null = null;

/** Drop a floor made early that nobody is going to walk into. */
function dropPrefetch(): void {
  // A built world owns geometry and materials; letting the reference go would
  // leave both on the GPU until the context died.
  if (prefetched?.world) disposeTree(prefetched.world.group);
  prefetched = null;
}
let order: MapEntry[] = [];

/**
 * Build floor `depth`.
 *
 * The library is the source; the generator is the fallback for a map that
 * somehow fails its gates at runtime, so a run can never dead-end on a bad
 * floor even if one slips through the build.
 */
function make(depth: number): GeneratedLevel | null {
  const entry = order[(depth - 1) % order.length];
  if (entry) {
    try {
      return loadMap(entry, depth, `${seed}:${depth}`);
    } catch (err) {
      console.warn(`map ${entry.id} failed to load, generating instead`, err);
    }
  }
  const result = generateLevel({ seed: `${seed}:${depth}`, depth, minRoutes: 2 });
  return result.ok ? (result as GeneratedLevel) : null;
}

function load(depth: number): void {
  let gen = prefetched?.depth === depth ? prefetched.gen : null;
  const ready = prefetched?.depth === depth ? prefetched.world : undefined;
  // Taken, not dropped: the world about to be used must not be disposed.
  if (ready) prefetched = null;
  else dropPrefetch();
  if (!gen) gen = make(depth);
  if (!gen) {
    // The gates rejected every candidate for this seed. Rather than ship a
    // broken floor, take a different seed — the player never learns this
    // happened, which is the point of gating at all.
    seed = freshSeed();
    load(depth);
    return;
  }

  if (world) {
    scene.remove(world.group);
    world.dispose();
  }
  for (const l of lights) {
    scene.remove(l);
    // The sky comes back with the lights and owns a shader material of its own.
    if ((l as THREE.Mesh).isMesh) disposeTree(l);
  }
  scene.clear();
  // The camera carries the view model and `run.root` carries every enemy,
  // pickup and tracer; clearing the scene detaches both, and forgetting to put
  // them back leaves a level where you take damage from things that are being
  // simulated but never drawn.
  scene.add(camera, run.root);

  /*
   * The world first, then the light.
   *
   * The shadow camera is fitted to the level's own extent and the level does
   * not exist until it is built, so this cannot be the other way round — a rig
   * built first would have to guess at bounds, and a guessed shadow camera is
   * a shadow map spending most of its resolution on empty ground.
   */
  world = ready ?? buildWorld(gen, quality);
  scene.add(world.group);
  lights = applyTheme(scene, gen.theme, renderer, gen.spec.id, world.bounds, quality.tier);

  /*
   * Compile everything before the first frame anybody sees.
   *
   * After the lights, because the light count is part of every program's key,
   * and before the player has control, because this is where the hitch has to
   * land. Half a second the first time a hostile appears is not a frame drop,
   * it is the moment you needed most.
   */
  warmShaders(renderer, scene, camera, quality.tier);
  // Programs are only half of it; the decal atlas is a texture and wants
  // uploading here too rather than on the first round that hits a wall.
  run.warm(renderer);

  player.setColliders(world.colliders);
  player.killPlane = world.bounds.min.y - 6;
  run.enter(gen, world.colliders);
  vm.setWeapon(run.state.weapon);
  const entry = order[(depth - 1) % order.length];
  hud.setPlace(entry?.name ?? gen.theme.name, gen.theme.name);
  document.title = `Floor ${depth} · ${entry?.name ?? gen.theme.name}`;
  /*
   * The air of the place.
   *
   * Derived from the same architecture the look is: a map whose roofs are open
   * and whose blocks are low is mostly sky and gets wind; one built out of
   * sheds and silos gets a hum. Nothing new to author, and no two maps in the
   * library sound alike.
   */
  const blocks = gen.theme.blocks ?? [];
  const skyline = gen.theme.skyline ?? [];
  const industrial =
    (gen.theme.facade === 'industrial' ? 0.6 : 0.15) +
    0.1 * [...blocks, ...skyline].filter((b) => /shed|silo|chimney|cooling|crane|gantry|pylon|sawtooth/.test(b)).length;
  const open = gen.spec.layers.every((l) => l.roof === false) ? 0.75 : 0.3;
  sound.ambience(Math.min(1, open), Math.min(1, industrial), gen.theme.daylight === false);
  sound.descend();
  dropSplash();
}

function startRun(): void {
  touch.active = true;
  seed = freshSeed();
  order = runOrder(hash32(`${seed}:order`));
  run.startRun(seed);
  load(1);
  hud.hideScreens();
}

function descend(): void {
  load(run.state.depth + 1);
}

// -----------------------------------------------------------------------------
// Input

// A mouse aims for itself; a thumb does not. Magnetism is a subtle pull that
// only exists on touch, and it never pulls the trigger — you always do that.
function applyMode(m: 'touch' | 'desktop'): void {
  run.assistStrength = m === 'touch' ? 1 : 0;
  hud.setHint(
    m === 'touch'
      ? 'Left thumb moves · drag right to look · hold the circle to fire'
      : 'WASD move · mouse look · click to fire · R reload · Q swap',
  );
  hud.setTouch(m === 'touch');
}
touch.onMode = applyMode;
// The touch controls swallow pointerdown on the play surface, so the audio
// context needs its own way to hear about the first tap.
addEventListener('pointerdown', () => {
  sound.start();
  run.telemetry.input();
}, { capture: true });
applyMode(touch.mode);

addEventListener('keydown', (e) => {
  // Browsers will not let a page make a sound before somebody touches it, and
  // rightly. Every input path calls this; it is cheap and idempotent.
  sound.start();
  // And the same place is where "the player has worked out how to play" is
  // first true, whichever of the three input paths they found.
  run.telemetry.input();
  if (paused) setPaused(false);
  if (e.code === 'KeyM') {
    settings.sound = sound.toggle();
    saveSettings(settings);
    run.say(settings.sound ? 'Sound on' : 'Sound off', 1.4);
  }
  if (e.code === 'Escape') setPaused(!paused);
  if (e.code === 'KeyR') run.reload();
  else if (e.code === 'KeyQ') run.swap();
  player.onKey(e, true);
  if (e.code === 'Space') e.preventDefault();
});
addEventListener('keyup', (e) => player.onKey(e, false));
addEventListener('blur', () => {
  player.clearKeys();
  run.triggerHeld = false;
});

/**
 * Pointer lock is what a mouse-driven shooter needs, but it is not always
 * available — an embedded frame refuses it outright — and Chrome briefly
 * refuses to re-lock right after Escape. Neither should cost you the game, so
 * a refusal falls back to drag-to-look and play continues.
 */
let lockDenied = false;
let dragging = false;

renderer.domElement.addEventListener('mousedown', (e) => {
  sound.start();
  if (hud.screenOpen) return;
  if (paused) {
    setPaused(false);
    return;
  }
  if (!lockDenied && document.pointerLockElement !== renderer.domElement) {
    const req = renderer.domElement.requestPointerLock?.() as unknown as Promise<void> | undefined;
    if (req && typeof req.catch === 'function') req.catch(() => { lockDenied = true; });
    return;
  }
  if (e.button === 0) {
    dragging = true;
    run.triggerHeld = true;
    run.triggerPressed = true;
  }
});
addEventListener('mouseup', (e) => {
  if (e.button === 0) {
    run.triggerHeld = false;
    dragging = false;
  }
});
addEventListener('mousemove', (e) => {
  if (document.pointerLockElement === renderer.domElement) player.look(e.movementX, e.movementY, settings.mouseLook);
  else if (lockDenied && dragging) player.look(e.movementX, e.movementY, settings.mouseLook);
});
/**
 * Pausing when the pointer lock goes away.
 *
 * Losing the lock means they alt-tabbed, and dying while looking at another
 * window is the least fair thing a shooter can do — so the run stops. The
 * trap is that "not locked" is also the state you are in when the lock was
 * never granted in the first place, which is every embedded frame and any
 * browser that refuses it. Pausing on that freezes the game at full frame
 * rate with nothing on screen to say why, and nothing that can undo it,
 * because only *acquiring* a lock cleared the flag.
 *
 * So: only a lock we actually held can pause the game, any input resumes it,
 * and while it is paused it says so.
 */
let paused = false;
let everLocked = false;

function setPaused(on: boolean): void {
  if (paused === on) return;
  paused = on;
  if (on) hud.buildSettings(settings);
  hud.setPaused(on);
  touch.active = !on;
  if (!on) last = performance.now();
}

/*
 * The settings, and what each of them actually does.
 *
 * Applied the moment a control moves rather than on an apply button: the one
 * setting a player has to feel out by trial is the look speed, and a slider
 * you cannot feel while you move it is a slider you cannot set.
 *
 * Quality is the exception and says so on screen. The resolution follows
 * immediately, but how much town is built is decided when a floor is made, so
 * it lands on the next one.
 */
const settings = loadSettings();

function applySettings(): void {
  saveSettings(settings);
  if (sound.enabled !== settings.sound) sound.toggle();
  if (settings.sound) sound.start();
  touch.lookSensitivity = settings.touchLook;
  player.invertY = settings.invertY;
  const tier = settings.quality === 'auto' ? detectTier() : settings.quality;
  if (tier !== quality.tier) {
    Object.assign(quality, qualityFor(tier));
    governor.retier(quality);
    renderer.setPixelRatio(governor.pixelRatio);
  }
}

hud.onSettings = applySettings;
hud.onResume = () => setPaused(false);
hud.onGear(() => setPaused(true));
applySettings();

document.addEventListener('pointerlockchange', () => {
  const locked = document.pointerLockElement === renderer.domElement;
  if (locked) {
    everLocked = true;
    setPaused(false);
    return;
  }
  player.clearKeys();
  run.triggerHeld = false;
  if (everLocked && touch.mode === 'desktop' && run.state.phase === 'playing') setPaused(true);
});

/*
 * The size of the part you can actually see.
 *
 * `innerHeight` is the layout viewport, and on a phone in landscape that is
 * not what is on the screen: the browser's own chrome overlaps it, the number
 * does not change when the toolbars slide away, and it is reported wrong
 * during a rotation. The canvas was sized from it, so the picture was taller
 * than the window and the bottom of it — where the trigger and the health bar
 * live — sat underneath Safari's toolbar.
 *
 * `visualViewport` is the one that means what it says. Where it is missing
 * the old numbers are still there to fall back on.
 */
function viewSize(): { w: number; h: number } {
  const vv = window.visualViewport;
  return {
    w: Math.max(1, Math.round(vv?.width ?? innerWidth)),
    h: Math.max(1, Math.round(vv?.height ?? innerHeight)),
  };
}

function fitToWindow(): void {
  const { w, h } = viewSize();
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  vm.resize(w, h);
  renderer.setPixelRatio(governor.pixelRatio);
  renderer.setSize(w, h);
  post.resize(w, h);
}

addEventListener('resize', fitToWindow);
// The visual viewport moves without a window resize — a toolbar sliding away
// is exactly that — so it has to be listened to on its own.
window.visualViewport?.addEventListener('resize', fitToWindow);
window.visualViewport?.addEventListener('scroll', fitToWindow);
/*
 * And again after the rotation settles.
 *
 * iOS reports the wrong size until the animation finishes, and once was not
 * enough: the first landscape frame came out at the portrait height and stayed
 * there until something else forced a resize. Three goes across a second costs
 * nothing and covers the slow ones.
 */
addEventListener('orientationchange', () => {
  for (const wait of [60, 350, 900]) setTimeout(fitToWindow, wait);
});
// The renderer was sized from `innerWidth` before any of this existed; correct
// it now that the honest numbers are available.
fitToWindow();

// -----------------------------------------------------------------------------

let phaseSeen = 'playing';
let last = performance.now();
/** Metres covered since the last footfall. */
let walked = 0;
/** Seconds until the next heartbeat. */
let beat = 0;

function frame(now: number): void {
  requestAnimationFrame(frame);
  const elapsed = now - last;
  const dt = Math.min(0.05, elapsed / 1000);
  last = now;

  run.telemetry.frame(elapsed);
  const ratio = governor.sample(elapsed, now);
  if (ratio !== null) {
    renderer.setPixelRatio(ratio);
    // The composer keeps its own targets, and a target still at the old ratio
    // is the resolution the governor just decided this machine cannot afford.
    post.setPixelRatio(ratio);
  }

  const open = hud.screenOpen || paused;
  if (!open && world) {
    if (touch.mode === 'touch') {
      const look = touch.consumeLook();
      player.look(look.x, look.y, settings.touchLook);
      player.analog = { x: touch.state.moveX, y: -touch.state.moveY };
      const p = touch.consumePresses();
      if (p.jump) player.requestJump();
      if (p.reload) run.reload();
      if (p.swap) run.swap();
      if (p.fireEdge) run.triggerPressed = true;
      run.triggerHeld = touch.state.firing;
      if (touch.state.firing || touch.state.moveX || touch.state.moveY) run.telemetry.input();
    } else {
      player.analog = null;
    }
    player.update(dt);
    run.update(dt);
    sound.setListener(camera.position.x, camera.position.y, camera.position.z, player.yaw);
    sound.beacon(run.exitSound);
    /*
     * Your own footsteps, by distance rather than by time.
     *
     * Timed footfalls moonwalk the moment anything changes the player's speed
     * — a card, a slope, a slowed step — and the mismatch is more noticeable
     * than no footsteps at all.
     */
    /*
     * The heartbeat, on the last third of your health.
     *
     * Timed rather than tied to the animation, and it speeds up as it gets
     * worse — the rate is the information, so a glance at the health bar is
     * not required to know how much trouble you are in.
     */
    const left = run.state.health / Math.max(1, run.state.maxHealth);
    if (run.state.phase === 'playing' && left < 0.36) {
      const urgency = 1 - left / 0.36;
      beat -= dt;
      if (beat <= 0) {
        sound.heartbeat(urgency);
        beat = 1.25 - urgency * 0.55;
      }
    } else {
      beat = 0;
    }

    if (player.grounded) {
      walked += Math.hypot(player.velocity.x, player.velocity.z) * dt;
      if (walked > 2.1) {
        walked = 0;
        sound.step(true);
      }
    } else {
      walked = Math.min(walked, 1.6);
    }
  } else {
    run.triggerHeld = false;
  }
  vm.update(dt, run.state, player);
  hud.update(run.state);

  if (run.state.phase !== phaseSeen) {
    phaseSeen = run.state.phase;
    if (phaseSeen === 'cleared') onCleared();
    else if (phaseSeen === 'dead') onDied();
  }
  if (contextLost) return;
  // The reflecting floor only renders its second view while you are in the
  // room it belongs to; asked every frame because "which room" changes.
  world?.reflections?.update(camera.position);
  post.render();
  // The weapon goes down last, over a cleared depth buffer, so it is always in
  // front of the level and never fights it for the same pixels.
  vm.render(renderer);
}

function onCleared(): void {
  // The same seed offers the same cards on the same floor, so a replay is a
  // replay rather than a similar-looking run.
  const cards = offer(run.state.depth, run.state.cards, stream(`${seed}:cards:${run.state.depth}`));
  run.telemetry.offer(cards.map((c) => c.id));
  document.exitPointerLock?.();
  // Build the next floor while they are reading three cards. By the time they
  // pick one it is already compiled, so descending is a cut, not a wait.
  const next = run.state.depth + 1;
  afterPaint(() => {
    const gen = make(next);
    if (!gen) return;
    prefetched = { depth: next, gen };
    // The plan first, then the geometry on the frame after, so the card screen
    // paints between the two rather than after both.
    afterPaint(() => {
      if (prefetched?.depth !== next) return;
      prefetched.world = buildWorld(gen, quality);
    });
  });
  hud.showCards(cards, run.state.depth, (card) => {
    run.applyCard(card);
    touch.active = true;
    phaseSeen = 'playing';
    descend();
    vm.setWeapon(run.state.weapon);
  }, run.state.cards);
}

function onDied(): void {
  touch.active = false;
  sound.hush();
  sound.died();
  document.exitPointerLock?.();
  const s = run.state;
  const row: ScoreRow = {
    at: Date.now(),
    score: s.score,
    depth: s.depth,
    kills: s.kills,
    time: Math.round(s.elapsed),
    seed,
    fresh: true,
  };
  // A run that scored nothing on the first floor is someone alt-tabbing, not a
  // result. Showing it back to them as a personal best is worse than useless.
  const counts = s.score > 0 || s.depth > 1;
  const rows = counts ? saveScore(row) : loadScores();
  hud.showDeath(s, rows, row, () => {
    phaseSeen = 'playing';
    startRun();
  });
}

/**
 * Detach and look at the map from a given point, without dying for it.
 *
 * Screenshotting a level means standing somewhere no player would stand, for
 * longer than a player would survive there. So this puts the player in fly
 * mode, moves the *player* (moving only the camera desyncs the two and the
 * next frame snaps back), aims it, and turns off the things that would
 * otherwise end the visit.
 */
function look(from: [number, number, number], at: [number, number, number]): string {
  player.mode = 'fly';
  player.killPlane = -1e9;
  player.pos.set(from[0], from[1], from[2]);
  player.velocity.set(0, 0, 0);
  const d = new THREE.Vector3(at[0] - from[0], at[1] - from[1], at[2] - from[2]).normalize();
  player.yaw = Math.atan2(-d.x, -d.z);
  player.pitch = Math.asin(THREE.MathUtils.clamp(d.y, -1, 1));
  run.godMode = true;
  return `looking from ${from.join(',')} at ${at.join(',')}`;
}

// A handle for poking at a live run from the console while developing.
if (import.meta.env?.DEV) {
  (globalThis as Record<string, unknown>).__game = {
    scene, camera, player, run, hud, touch, THREE, ENEMY_TYPES, vm, quality, governor, renderer, sound,
    get world() { return world; },
    load,
    seedOf: () => seed,
    /**
     * What the run has actually done, as a table.
     *
     * `__game.stats()` in the console. Printed rather than returned, because
     * a formatted table is the whole point — nobody reads four kilobytes of
     * nested objects, and a measurement nobody reads is not a measurement.
     */
    stats(): string {
      const r = run.telemetry.snapshot(run.state);
      // eslint-disable-next-line no-console
      console.log(report(r));
      return `${r.floors.length} floor(s) recorded — see above, or __game.telemetry() for the data`;
    },
    telemetry: () => run.telemetry.snapshot(run.state),
    /** Line every enemy type up in front of the camera, for eyeballing silhouettes. */
    parade(): string[] {
      return run.parade(ENEMY_TYPES);
    },
    look,
    /**
     * Stand somewhere with something to look at.
     *
     * Picking screenshot positions by hand means picking them by guessing, and
     * most guesses in a map made of corridors land against a wall. This scores
     * the level's own enemy posts by how far you can see from them and stands
     * on the winner, facing the way that was furthest.
     */
    vista(): string {
      if (!world) return 'no world';
      const posts = (run as unknown as { gen: { enemyPosts: [number, number, number][] } | null }).gen
        ?.enemyPosts;
      if (!posts?.length) return 'no posts';
      const ray = new THREE.Raycaster();
      // Only the map's own walls, and only a dozen posts. Raycasting the whole
      // group means raycasting the town as well, which is a hundred thousand
      // triangles a ray and takes minutes rather than milliseconds.
      const meshes: THREE.Object3D[] = world.group.children.filter(
        (o) => o instanceof THREE.Mesh && /static_hard|exterior_wall/.test(o.name),
      );
      const sample: [number, number, number][] = [];
      for (let i = 0; i < Math.min(12, posts.length); i++)
        sample.push(posts[Math.floor((i / 12) * posts.length)]);
      let best: { p: THREE.Vector3; dir: THREE.Vector3; d: number } | null = null;
      for (const c of sample) {
        const p = new THREE.Vector3(c[0], c[2] + 1.7, -c[1]);
        for (let i = 0; i < 12; i++) {
          const a = (i / 12) * Math.PI * 2;
          const dir = new THREE.Vector3(Math.cos(a), 0, Math.sin(a));
          ray.set(p, dir);
          ray.far = 90;
          const hit = ray.intersectObjects(meshes, true)[0];
          const d = hit ? hit.distance : 90;
          if (!best || d > best.d) best = { p, dir, d };
        }
      }
      if (!best) return 'nothing';
      const at = best.p.clone().addScaledVector(best.dir, Math.min(30, best.d));
      at.y = best.p.y + 1.5;
      return look([best.p.x, best.p.y, best.p.z], [at.x, at.y, at.z]) + ` (${best.d.toFixed(0)}m)`;
    },
    /** Jump straight to a named map, for checking how one looks. */
    goto(id: string, depth = 1): string {
      const entry = MAPS.find((m) => m.id === id);
      if (!entry) return `no map "${id}"`;
      // The slot the run will actually read for this depth, not the front of
      // the list: `load` indexes by depth, so putting the map at the head sent
      // you to whatever was already sixth.
      order = [...order];
      order[(depth - 1) % order.length] = entry;
      dropPrefetch();
      load(depth);
      return document.title;
    },
  };
}

// The first thing that happens is the game.
if (requested) seed = requested;
else if (daily) seed = dailySeed();
order = runOrder(hash32(`${seed}:order`));
run.startRun(seed);
requestAnimationFrame(frame);
afterPaint(() => {
  load(1);
  if (daily) run.sayLater("Today's run — the same one everybody gets", 1.2, 3.4);
  const best = loadScores();
  if (best.length) run.say(`Best so far: floor ${Math.max(...best.map((r) => r.depth))}`, 4);
});

// A weapon swap can happen from a pickup, so keep the model honest.
let shownWeapon = run.state.weapon.id;
setInterval(() => {
  if (run.state.weapon.id !== shownWeapon) {
    shownWeapon = run.state.weapon.id;
    vm.setWeapon(run.state.weapon);
  }
}, 120);
